// src/components/cars/VehicleAdditionalAttribute.jsx
import { useTheme, useMediaQuery } from '@mui/material';
import React, { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useDispatch, useSelector } from 'react-redux';
import {
  Button,
  TextField,
  Box,
  Typography,
  FormControl,
  Select,
  FormHelperText,
} from "@mui/material";
import {
  alphanumericWithSpaceValidation,
  validateEmail,
  validateField,
} from "../../common/helpers/validation";
import {
  CommonYesNo,
  Airbags,
  antiLockBrakes
} from "../../common/StaticFieldList";
import {
  createVehicleInfo,
  getAllVehicleInfo,
  getVehicleInfoByVIN,
  updateVehicleInfo,
} from "../../redux/actions/vehicleInfoActions";
import { populateValue } from "../../common/helpers/utils";

const requiredFields = ["car_type", "model_year", "zipcode"];

const validationRulesAdditionalAttributes = [
  { fieldName: "symbol", label: "Symbol", type: "text" , id: "symbol" },
  { fieldName: "airBags", label: "Air Bags", type: "select", option: Airbags, id: "airBags" },
  { fieldName: "antiLockBrakes", label: "Anti-lock Brakes", type: "select", option: antiLockBrakes , id: "antiLockBrakes" },
  { fieldName: "runningLights", label: "Running Lights", type: "select", option: CommonYesNo, id: "runningLights" },
];

const validationFunctions = {
  firstName: alphanumericWithSpaceValidation,
  lastName: alphanumericWithSpaceValidation,
  email: validateEmail,
};

const VehicleAdditionalAttribute = forwardRef(({
  setShowHideVehicleForm,
  vehicleData,
  vehicleInfoData,
  vehicleAttrData,
  handleBackward,
  searchQuoteId,
  onComplete,
}, ref) => {
  const dispatch = useDispatch();
  const getGpId = useSelector((state) => state.generalPartyInfo.gpId);
  let quoteId = getGpId;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
// const VehicleInfo = useSelector((state) => state.vehicleInfo.savedVehicleInfo);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const newFieldArg = [...validationRulesAdditionalAttributes];

  const [formState, setFormState] = useState({
    formData: newFieldArg.reduce((fields, { fieldName }) => ({ ...fields, [fieldName]: "" }), {}),
    touchedFields: newFieldArg.reduce((fields, { fieldName }) => ({ ...fields, [fieldName]: false }), {}),
    fieldErrors: newFieldArg.reduce((errors, { fieldName }) => ({ ...errors, [fieldName]: "" }), {}),
  });

  const [isDirty, setIsDirty] = useState(false);
  const { formData, touchedFields, fieldErrors } = formState;
  const dynamicTheme = useSelector((state) => state?.theme?.theme[0]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setIsDirty(true);
    setFormState((prevState) => ({
      ...prevState,
      formData: { ...prevState.formData, [name]: value },
    }));
  };

  const handleBlur = (field) => {
    if (!requiredFields.includes(field)) return;

    const value = formData[field];
    const error = validateField(value, newFieldArg.find((rule) => rule.fieldName === field).label);
    const additionalError = validationFunctions[field] ? validationFunctions[field](value) : "";

    setFormState((prevFormState) => ({
      ...prevFormState,
      fieldErrors: { ...prevFormState.fieldErrors, [field]: error || additionalError },
    }));
  };

   const handleBackSubmit = () => {
      handleBackward(vehicleAttrData)
    };
  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = newFieldArg.reduce((errors, { fieldName, label }) => {
      if (requiredFields.includes(fieldName)) {
        const value = formData[fieldName];
        const error = validateField(value, label);
        const additionalError = validationFunctions[fieldName] ? validationFunctions[fieldName](value) : "";

        setFormState((prevFormState) => ({
          ...prevFormState,
          fieldErrors: { ...prevFormState.fieldErrors, [fieldName]: error || additionalError },
        }));

        return { ...errors, [fieldName]: error || additionalError };
      }
      return errors;
    }, {});

    const hasErrors = Object.values(validationErrors).some((error) => !!error);

    if (!hasErrors) {
      try {
        if (vehicleData.id) {
          const newFormData = {
            ...formData,
            ...vehicleInfoData,
            ...vehicleAttrData,
            id: vehicleData.id,
            vehicleNumber: vehicleData.vehicleNumber,
            gpId : quoteId ? quoteId : searchQuoteId,
          };
          await dispatch(updateVehicleInfo(newFormData, () => {}));
        } else {
          const newFormData = {
            ...vehicleInfoData,
            ...vehicleAttrData,
            ...formData,
            gpId : quoteId ? quoteId : searchQuoteId,
          };
          await dispatch(createVehicleInfo(newFormData, () => {}));
        }
         await dispatch(getAllVehicleInfo(quoteId ? quoteId : searchQuoteId));
      } catch (error) {
        // swallow to keep sample concise
      }
    }
     setIsDirty(false);
    //  if (typeof onComplete === 'function') onComplete();
    setShowHideVehicleForm(false);
  };


  useEffect(() => {
    setFormState((prevFormState) => ({
      ...prevFormState,
      formData: newFieldArg.reduce(
        (fields, { fieldName }) => ({
          ...fields,
          [fieldName]:
            (vehicleData && populateValue(vehicleData[fieldName], fieldName, newFieldArg)) || "",
        }),
        {}
      ),
    }));
  }, [vehicleData]);

 useImperativeHandle(ref, () => ({
  shouldConfirm: () => isDirty,
   saveWithoutNav: async () => ({ ok: true }),
   resetForm: () => {
     setFormState((prev) => ({
       ...prev,
       formData: Object.fromEntries(Object.keys(prev.formData).map((k) => [k, ""])),
       touchedFields: Object.fromEntries(Object.keys(prev.touchedFields).map((k) => [k, false])),
       fieldErrors: Object.fromEntries(Object.keys(prev.fieldErrors).map((k) => [k, ""])),
     }));
     setIsDirty(false);
   },
 }));

  return (
    <>
      <Box>
        <Box
          sx={{
            alignSelf: "stretch",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "flex-start",
            gap: "36px",
            maxWidth: "100%",
            '@media (min-width: 768px) and (max-width: 1024px)': { minHeight: '55vh', height: 'auto', overflowY: 'auto' },
            '@media (min-width: 1024px) and (min-height: 1366px)': { height: 'auto', minHeight: '75vh' },
          }}
        >
          <section
            sx={{
              alignSelf: "stretch",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: "0px 23px 0px 20px",
              boxSizing: "border-box",
              maxWidth: "100%",
              textAlign: "left",
              fontSize: "14px",
              color: dynamicTheme?.secondaryColor,
              fontFamily: '"Poppins", sans-serif',
            }}
          >
            <Box
              id="form"
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: isMobile ? "100%" : isTablet ? "90%" : "853px",
                borderRadius: "4px",
                backgroundColor: "#fff",
                border: "1px solid #e3e3e3",
                boxSizing: "border-box",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: isMobile ? "20px 10px" : "30px 0px 24px",
                gap: "23px",
                maxWidth: "100%",
              }}
            >
              <Typography
                id="vehicleadditionalattributesheading"
                variant="h1"
                component="div"
                sx={{
                  border: "none",
                  backgroundColor: "transparent",
                  alignSelf: "stretch",
                  height: "51px",
                  fontFamily: '"Poppins", sans-serif',
                  fontWeight: "600",
                  fontSize: isMobile ? "20px" : "25px",
                  color: dynamicTheme?.secondaryColor,
                  paddingLeft: isMobile ? "20px" : "50px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                Vehicle Additional Attributes
              </Typography>
              <hr style={{ borderTop: "1px solid #ccc", width: "100%" }} />
              <Box
                sx={{
                  alignSelf: "stretch",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "flex-end",
                  padding: isMobile ? "0px 20px" : "0px 44px 0px 52px",
                  boxSizing: "border-box",
                  maxWidth: "100%",
                  fontFamily: '"Poppins", sans-serif',
                }}
              >
                <Box
                  sx={{
                    flex: "1",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                    justifyContent: "flex-start",
                    gap: "16px",
                    maxWidth: "100%",
                    fontFamily: '"Poppins", sans-serif',
                  }}
                >
                  <Box
                    sx={{
                      flex: "1",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      justifyContent: "flex-start",
                      gap: "4px",
                      minWidth: isMobile ? "100%" : "240px",
                      maxWidth: "100%",
                      fontFamily: '"Poppins", sans-serif',
                    }}
                  >
                    <Box
                      sx={{
                        alignSelf: "stretch",
                        display: "grid",
                        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                        gap: "5px 20px",
                        flexDirection: "column",
                        alignItems: "flex-start",
                        justifyContent: "flex-start",
                        padding: "0px 0px 8px",
                      }}
                    >
                      {validationRulesAdditionalAttributes.map(
                        ({ fieldName, label, type, option }, index) => (
                          <Box
                            key={index}
                            sx={{ width: "100%", margin: "0 0 15px 0", display: "flex", flexFlow: "column" }}
                          >
                            <Typography
                              key={fieldName}
                              variant="body1"
                              sx={{
                                position: "relative",
                                lineHeight: "40px",
                                fontWeight: "500",
                                display: "inline-block",
                                fontFamily: '"Poppins", sans-serif',
                                minWidth: "83px",
                                zIndex: "1",
                              }}
                            >
                              {label}
                            </Typography>
                            {type === "select" ? (
                              <FormControl fullWidth variant="outlined"
                                sx={{ border: "none", backgroundColor: "transparent", alignSelf: "stretch", fontFamily: '"Poppins", sans-serif', fontWeight: "500", fontSize: "16px", zIndex: "1" }}
                                size={isMobile ? "small" : "medium"}
                              >
                                <Select
                                  native
                                  name={fieldName}
                                  value={formData[fieldName] || ""}
                                  onChange={handleInputChange}
                                  onBlur={() => handleBlur(fieldName)}
                                  error={touchedFields[fieldName] && !!fieldErrors[fieldName]}
                                  displayEmpty
                                  required={requiredFields.includes(fieldName)}
                                  id={fieldName}
                                  inputProps={{ 'aria-label': label }}
                                  sx={{
                                    height: isMobile ? "42px" : "52px",
                                    "& .MuiInputBase-root": {
                                      height: isMobile ? "42px" : "52px",
                                      backgroundColor: "#fff",
                                      fontSize: "14px",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& .MuiInputBase-input": {
                                      color: dynamicTheme?.secondaryColor,
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                    "& fieldset": {
                                      borderColor: touchedFields[fieldName] && !!fieldErrors[fieldName] ? "#d32f2f" : "#C1C1C1",
                                      fontFamily: '"Poppins", sans-serif',
                                    },
                                  }}
                                >
                                  <option value="" disabled>
                                    Select {label}
                                  </option>
                                  {option && option.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </Select>
                                {touchedFields[fieldName] && fieldErrors[fieldName] && (
                                  <FormHelperText error>{fieldErrors[fieldName]}</FormHelperText>
                                )}
                              </FormControl>
                            ) : (
                              <TextField
                                style={{
                                  border: "none",
                                  backgroundColor: "transparent",
                                  alignSelf: "stretch",
                                  height: isMobile ? "42px" : "52px",
                                  fontFamily: '"Poppins", sans-serif',
                                  fontWeight: "500",
                                  fontSize: "16px",
                                  color: dynamicTheme?.secondaryColor,
                                  zIndex: "1",
                                }}
                                type={type}
                                variant="outlined"
                                required={requiredFields.includes(fieldName)}
                                id={fieldName}
                                placeholder={label}
                                name={fieldName}
                                autoComplete={fieldName}
                                value={formData[fieldName]}
                                onChange={(e) => {
                                  if (type === "text" && e.target.value.length > 10) return;
                                  if ((fieldName === "symbol" || fieldName === 'antiLockBrakes') && !/^[A-Za-z0-9]*$/.test(e.target.value)) return;
                                  handleInputChange(e);
                                }}
                                onBlur={() => handleBlur(fieldName)}
                                error={touchedFields[fieldName] && !!fieldErrors[fieldName]}
                                helperText={touchedFields[fieldName] ? fieldErrors[fieldName] : ""}
                                size={isMobile ? "small" : "medium"}
                                sx={{
                                  "& fieldset": { borderColor: "#C1C1C1", fontFamily: '"Poppins", sans-serif' },
                                  "& .MuiInputBase-root": {
                                    height: isMobile ? "42px" : "52px",
                                    backgroundColor: "#fff",
                                    fontSize: "16px",
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                  "& .MuiInputBase-input": {
                                    color: dynamicTheme?.secondaryColor,
                                    fontFamily: '"Poppins", sans-serif',
                                  },
                                }}
                              />
                            )}
                          </Box>
                        )
                      )}
                    </Box>
                  </Box>
                </Box>
              </Box>
              <div
                id="buttoncontainer"
                style={{
                  width: isMobile ? "100%" : "222px",
                  height: "42px",
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  gap: "14px",
                  fontFamily: '"Poppins", sans-serif',
                  padding: isMobile ? "0 20px" : "0",
                }}
              >
                <Button
                  id="backbutton"
                  
                  startIcon={<ArrowBackIosIcon />}
                  disableElevation
                  variant="outlined"
                  onClick={handleBackSubmit}
                >
                  Back
                </Button>

                <Button
                  id="nextbutton"
                  type="submit"
                  endIcon={<ArrowForwardIosIcon />}
                  disableElevation
                  variant="outlined"
                >
                  Next
                </Button>
              </div>
            </Box>
          </section>
        </Box>
      </Box>
    </>
  );
});

export default VehicleAdditionalAttribute;
